import { useCallback, useMemo, useState } from 'react'

import { useForm, y } from '@circlefin/form'
import { ispbMap } from '@shared/components/common/AccountInformation/Pix/ispbMap'
import { matchSorter } from 'match-sorter'
import useTranslation from 'next-translate/useTranslation'

import { useLinkPix } from '../../../Create/variants'
import { useLinkBankAccount } from '../../../Link.Context'

const schema = y.object({
  /**
   * Bank Account Name.
   */
  bankName: y.string().max(35, { key: 'max', max: 35 }).required(),
  /**
   * Bank Account Nickname.
   */
  nickname: y.string().required(),
  /**
   * Bank ID.
   */
  bankId: y
    .string()
    .oneOf(ispbMap.map(({ ispb }) => ispb))
    .required(),
  /**
   * Bank Account Number.
   */
  accountNumber: y
    .string()
    .required()
    .matches(/^\d+$/, {
      message: {
        key: 'numeric',
      },
    })
    .max(20, { key: 'number.max', max: 20 }),
  /**
   * Account Type.
   */
  accountType: y.string().required(),
  /**
   * Company Tax ID.
   */
  taxId: y.string().cnpj().required(),
  /**
   * Branch Code.
   */
  branchCode: y
    .string()
    .required()
    .matches(/^\d+$/, {
      message: {
        key: 'numeric',
      },
    })
    .max(4, { key: 'number.max', max: 4 }),
})

const accTypesShortcut = 'link.accountInformation.pixAccountTypes'

const ispbDropdownItems = ispbMap.map((bank) => {
  return {
    label: bank.name,
    value: bank.ispb,
  }
})

export type PixFormValues = y.InferType<typeof schema>

export interface PixFormProps {
  /**
   * On Form Submit.
   */
  onSubmit?: (values: PixFormValues) => void
}

export const PixForm: React.FC<PixFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation('modals.bankAccount')
  const { t: tCommon } = useTranslation('common')

  const [{ information }, { setInformation }] = useLinkBankAccount()
  const [searchTerm, setSearchTerm] = useState('')
  const [createPix] = useLinkPix()
  const filtered = useMemo(() => {
    if (searchTerm) {
      return matchSorter(ispbDropdownItems, searchTerm, {
        keys: ['label'],
      })
    }

    return undefined
  }, [searchTerm])

  const accountTypes = [
    {
      label: t(`${accTypesShortcut}.checking`),
      value: 'checking',
    },
    {
      label: t(`${accTypesShortcut}.savings`),
      value: 'savings',
    },
    {
      label: t(`${accTypesShortcut}.prepaid`),
      value: 'prepaid',
    },
    {
      label: t(`${accTypesShortcut}.salary`),
      value: 'salary',
    },
    {
      label: t(`${accTypesShortcut}.other`),
      value: 'other',
    },
  ]

  const [Form] = useForm<PixFormValues>({
    schema,
    mode: 'onBlur',
    defaultValues: {
      ...information.pix,
    },
  })

  const handleInputChange = useCallback((search: string) => {
    setSearchTerm(search)
  }, [])

  const onFormSubmit = useCallback(
    (pix: PixFormValues) => {
      // Set data to context
      setInformation({ pix })

      // Pass data to submit callback (for testing)
      onSubmit?.(pix)

      createPix({
        input: {
          accountNumber: pix.accountNumber,
          accountType: pix.accountType,
          ispb: pix.bankId,
          taxId: pix.taxId,
          branchCode: pix.branchCode,
          name: pix.bankName,
          nickname: pix.nickname,
        },
      })
    },
    [createPix, onSubmit, setInformation],
  )
  return (
    <div>
      <Form className="grid w-full gap-6" onSubmit={onFormSubmit}>
        <Form.Input
          className="w-full"
          data-testid="input-nickname"
          label={t('link.accountInformation.nickname')}
          name="nickname"
        />

        <Form.Input
          className="w-full"
          data-testid="input-bankName"
          label={t('link.accountInformation.bankHolderName')}
          name="bankName"
        />

        <Form.Combobox
          className="w-full"
          data-testid="input-bankId"
          filtered={filtered}
          items={ispbDropdownItems}
          label={t('link.accountInformation.ispb')}
          name="bankId"
          onInputChange={handleInputChange}
        />

        <Form.Input
          className="w-full"
          data-testid="input-branchCode"
          label={t('link.accountInformation.branchCode')}
          maxLength={4}
          name="branchCode"
        />

        <Form.Input
          className="w-full"
          data-testid="input-accountNumber"
          label={t('link.accountInformation.accountNumber')}
          name="accountNumber"
        />

        <Form.Dropdown
          className="w-full"
          data-testid="input-accountType"
          items={accountTypes}
          label={t('link.accountInformation.accountType')}
          name="accountType"
        />

        <Form.Input
          className="w-full"
          data-testid="input-taxId"
          label={t('link.accountInformation.taxId')}
          mask="cnpj"
          name="taxId"
          placeholder="**.***.***/****-**"
        />

        <Form.SubmitButton
          className="mt-3 w-full"
          data-testid="button-continue"
          variant="primary"
        >
          {tCommon('continue')}
        </Form.SubmitButton>
      </Form>
    </div>
  )
}
